import type { PerformanceResourceItem } from '@/app/lib/api/types-api'

import { FAQ_CATALOG, FAQ_SUBTYPE_CATALOG } from './catalog'
import { FAQ_MAX_ENTRIES, FAQ_MIN_ENTRIES } from './constants'
import { renderFaqTemplate } from './renderFaqTemplate'
import { resolveFaqCategory } from './resolveFaqCategory'
import { resolveFaqFacts } from './resolveFaqFacts'
import { resolveFaqSubtype } from './resolveFaqSubtype'
import type { FaqAccordionEntry, FaqFacts, FaqTemplate } from './types'

interface RenderedFaq {
  readonly template: FaqTemplate
  readonly entry: FaqAccordionEntry
  /** Posición original en el catálogo: desempata prioridades iguales. */
  readonly index: number
}

/** Renderiza un conjunto de plantillas descartando las que no tienen datos. */
function renderTemplates(templates: readonly FaqTemplate[], facts: FaqFacts): RenderedFaq[] {
  const rendered: RenderedFaq[] = []
  templates.forEach((template, index) => {
    const entry = renderFaqTemplate(template, facts)
    if (entry) rendered.push({ template, entry, index })
  })
  return rendered
}

function compareFaqs(a: RenderedFaq, b: RenderedFaq): number {
  return a.template.priority - b.template.priority || a.index - b.index
}

/**
 * Plantillas del mismo `topic`: se conserva la de menor `priority` (a igualdad,
 * la primera del catálogo). Las plantillas sin `topic` nunca se deduplican.
 */
function dedupeByTopic(items: readonly RenderedFaq[]): RenderedFaq[] {
  const winners = new Map<string, RenderedFaq>()
  for (const item of items) {
    const topic = item.template.topic
    if (!topic) continue
    const current = winners.get(topic)
    if (!current || compareFaqs(item, current) < 0) winners.set(topic, item)
  }
  return items.filter((item) => {
    const topic = item.template.topic
    return !topic || winners.get(topic) === item
  })
}

/** Render + dedupe + orden estable de un conjunto de plantillas. */
function resolveEntries(templates: readonly FaqTemplate[], facts: FaqFacts): RenderedFaq[] {
  return dedupeByTopic(renderTemplates(templates, facts)).sort(compareFaqs)
}

/**
 * Orquestador puro del motor de FAQs dinámicas de la ficha de evento (PDP):
 * sin red ni JSX. Compone `resolveFaqCategory` → `resolveFaqSubtype` →
 * `resolveFaqFacts` → `renderFaqTemplate` (specs/059-faqs-pdp, contrato §1).
 *
 * Si el evento tiene subtipo y su catálogo produce al menos `FAQ_MIN_ENTRIES`
 * preguntas, se usa; si no, el catálogo general de la categoría. Tras
 * deduplicar por `topic` y ordenar por `(priority, índice)` se corta a
 * `FAQ_MAX_ENTRIES`.
 *
 * Devuelve entre `FAQ_MIN_ENTRIES` y `FAQ_MAX_ENTRIES` entradas, o `[]` si la
 * categoría no resuelve o quedan menos del mínimo. Nunca lanza ante datos nulos.
 */
export function buildPerformanceFaqs(
  resource: PerformanceResourceItem,
  categoriaSlug: string,
  priceAmount: number | null,
): readonly FaqAccordionEntry[] {
  const category = resolveFaqCategory(categoriaSlug, resource.genres)
  if (!category) return []

  const subtype = resolveFaqSubtype(resource, category)
  const facts = resolveFaqFacts(resource, priceAmount, category)

  const subtypeTemplates = subtype ? FAQ_SUBTYPE_CATALOG[subtype] : undefined
  const subtypeEntries = subtypeTemplates ? resolveEntries(subtypeTemplates, facts) : []

  const chosen =
    subtypeEntries.length >= FAQ_MIN_ENTRIES
      ? subtypeEntries
      : resolveEntries(FAQ_CATALOG[category], facts)

  const entries = chosen.slice(0, FAQ_MAX_ENTRIES).map((item) => item.entry)
  return entries.length >= FAQ_MIN_ENTRIES ? entries : []
}
